import { ApiRequest } from "GlobalFunctions";
import { useEffect, useState, useRef } from "react";

interface SliderBanner {
  banner_id: number;
  image: string;
  url?: string;
}

export default function BannerSlider({ section = "slider-home" }: { section?: string }) {
  const [banners, setBanners] = useState<SliderBanner[]>([]);
  const [current, setCurrent] = useState(0);
  const [visible, setVisible] = useState(true);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    ApiRequest({
      url: '/banners/getSliderBanners',
      method: 'GET',
      query: { section },
      setResponse: (response: any) => {
        if (response.status === 'success' && response.banners?.length > 0) {
          setBanners(response.banners);
        }
      }
    });
  }, [section]);

  useEffect(() => {
    if (banners.length <= 1) return;
    timerRef.current = setInterval(() => {
      setCurrent(prev => (prev + 1) % banners.length);
    }, 10000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [banners.length]);

  const trackClick = (banner_id: number) => {
    ApiRequest({
      url: '/banners/registerClick',
      method: 'POST',
      body: { banner_id },
      setResponse: () => {},
    });
  };

  const goTo = (index: number) => {
    setCurrent(index);
    if (timerRef.current) clearInterval(timerRef.current);
    if (banners.length > 1) {
      timerRef.current = setInterval(() => {
        setCurrent(prev => (prev + 1) % banners.length);
      }, 10000);
    }
  };

  if (!visible) return null;
  if (banners.length === 0) return (
    <div style={{
      height: 240, width: '100%',
      background: 'linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)',
      backgroundSize: '200% 100%',
      animation: 'sliderSkeleton 1.4s ease infinite',
    }}>
      <style>{`@keyframes sliderSkeleton { 0%{background-position:200% 0} 100%{background-position:-200% 0} }`}</style>
    </div>
  );

  const pct = 100 / banners.length;

  return (
    <div style={{
      width: '100%',
      position: 'relative',
      overflow: 'hidden',
      background: '#0f172a',
      height: 240,
    }}>
      {/* Label publicidad */}
      <span style={{
        position: 'absolute', top: 10, left: 14, zIndex: 10,
        fontSize: 10, color: 'rgba(255,255,255,0.55)',
        background: 'rgba(0,0,0,0.45)', padding: '2px 8px',
        borderRadius: 4, textTransform: 'uppercase', letterSpacing: '0.8px',
        pointerEvents: 'none',
      }}>Publicidad</span>

      {/* Cerrar */}
      <button
        onClick={() => setVisible(false)}
        aria-label="Cerrar"
        style={{
          position: 'absolute', top: 8, right: 12, zIndex: 10,
          background: 'rgba(0,0,0,0.45)', border: 'none', color: '#fff',
          width: 30, height: 30, borderRadius: '50%', cursor: 'pointer',
          fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'background 0.2s',
        }}
        onMouseEnter={e => (e.currentTarget.style.background = 'rgba(0,0,0,0.7)')}
        onMouseLeave={e => (e.currentTarget.style.background = 'rgba(0,0,0,0.45)')}
      >✕</button>

      {/* Track de slides */}
      <div style={{
        display: 'flex',
        width: `${banners.length * 100}%`,
        height: '100%',
        transform: `translateX(-${current * pct}%)`,
        transition: 'transform 0.55s cubic-bezier(0.4, 0, 0.2, 1)',
      }}>
        {banners.map((b, i) => (
          <div key={b.banner_id} style={{ width: `${pct}%`, height: '100%', flexShrink: 0 }}>
            {b.url ? (
              <a
                href={b.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: 'block', height: '100%' }}
                onClick={() => trackClick(b.banner_id)}
              >
                <img
                  src={b.image}
                  alt="Publicidad"
                  fetchPriority={i === 0 ? 'high' : 'low'}
                  loading={i === 0 ? 'eager' : 'lazy'}
                  decoding="async"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
              </a>
            ) : (
              <img
                src={b.image}
                alt="Publicidad"
                fetchPriority={i === 0 ? 'high' : 'low'}
                loading={i === 0 ? 'eager' : 'lazy'}
                decoding="async"
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            )}
          </div>
        ))}
      </div>

      {/* Flechas prev/next */}
      {banners.length > 1 && (
        <>
          <button
            onClick={() => goTo((current - 1 + banners.length) % banners.length)}
            aria-label="Anterior"
            style={{
              position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)',
              background: 'rgba(0,0,0,0.45)', border: 'none', color: '#fff',
              width: 38, height: 38, borderRadius: '50%', cursor: 'pointer',
              fontSize: 22, display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 5, transition: 'background 0.2s',
            }}
            onMouseEnter={e => (e.currentTarget.style.background = 'rgba(0,0,0,0.7)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'rgba(0,0,0,0.45)')}
          >‹</button>
          <button
            onClick={() => goTo((current + 1) % banners.length)}
            aria-label="Siguiente"
            style={{
              position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)',
              background: 'rgba(0,0,0,0.45)', border: 'none', color: '#fff',
              width: 38, height: 38, borderRadius: '50%', cursor: 'pointer',
              fontSize: 22, display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 5, transition: 'background 0.2s',
            }}
            onMouseEnter={e => (e.currentTarget.style.background = 'rgba(0,0,0,0.7)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'rgba(0,0,0,0.45)')}
          >›</button>
        </>
      )}

      {/* Dots de navegación */}
      {banners.length > 1 && (
        <div style={{
          position: 'absolute', bottom: 14, left: 0, right: 0,
          display: 'flex', justifyContent: 'center', gap: 8, zIndex: 5,
        }}>
          {banners.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              aria-label={`Ir a slide ${i + 1}`}
              style={{
                width: i === current ? 26 : 8, height: 8,
                borderRadius: 4, border: 'none', padding: 0,
                cursor: 'pointer',
                background: i === current ? '#fff' : 'rgba(255,255,255,0.4)',
                transition: 'all 0.35s ease',
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
